<template>
  <div class="min-h-screen">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header Section -->
      <div>
        <div class="flex items-center justify-between element-island">
          <div>
            <h1 class="text-3xl font-bold text-gray-900">
              {{ project?.isClient ? $t('HeadContract.BillOfQuantities.clientTitle') : $t('HeadContract.BillOfQuantities.contractorTitle') }}
            </h1>

            <p class="mt-2 text-sm text-gray-600">
              {{ $t('HeadContract.BillOfQuantities.subTitle') }}
            </p>
          </div>

          <div
            v-if="projectsStore.project?.unarchived"
            class="flex items-center space-x-2"
          >
            <button
              v-if="!project?.boq_locked"
              type="button"
              class="btn btn--secondary-blue flex items-center space-x-2"
              data-test="add-trade"
              @click="lockBoq"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 10.5V6.75a4.5 4.5 0 1 1 9 0v3.75M3.75 21.75h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H3.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
</svg>

              <span>{{ $t('HeadContract.BillOfQuantities.button.lock') }}</span>
            </button>

            <button
              v-else-if="userStore.isRole(['admin', 'company_admin', 'project_admin', 'contract_admin'])"
              type="button"
              class="btn btn--primary-blue flex items-center space-x-2"
              @click="unlockBoq"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
</svg>

              <span>{{ $t('HeadContract.BillOfQuantities.button.unlock') }}</span>
            </button>

            <button
              v-else
              type="button"
              class="btn btn--primary-blue cursor-not-allowed flex items-center space-x-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
</svg>

              <span>{{ $t('HeadContract.BillOfQuantities.button.locked') }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-if="trades.length === 0 && fetchTradesAction.state.value === States.COMPLETE"
        class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-8"
      >
        <div class="px-6 py-16 text-center">
          <div class="mx-auto h-12 w-12 text-gray-300 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-12 h-12">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
          </div>

          <h3 class="text-sm font-medium text-gray-900 mb-1">
            {{ $t('HeadContract.BillOfQuantities.noTradesFound') }}
          </h3>

          <p class="text-sm text-gray-500 mb-6">
            {{ $t('HeadContract.BillOfQuantities.createFirstTrade') }}
          </p>

          <div class="flex flex-wrap items-center justify-center gap-3">
            <button
              v-if="canSeedTrades"
              type="button"
              class="btn btn--primary-blue flex items-center space-x-2"
              :disabled="generateTradesAction.is(States.LOADING)"
              data-test="generate-standard-trades"
              @click="seedStandardTrades"
            >
              <span>{{ $t('HeadContract.BillOfQuantities.button.generateStandardTrades') }}</span>
            </button>

            <button
              v-if="!isEditingDisabled"
              type="button"
              class="btn btn--secondary-blue"
              data-test="add-first-trade"
              @click="createTrade"
            >
              {{ $t('HeadContract.BillOfQuantities.button.addFirstTrade') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Main Content Card -->
      <div
        v-else-if="fetchTradesAction.state.value === States.COMPLETE"
        class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-8"
      >
        <!-- Table Header with Controls -->
        <div class="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <div class="space-y-4">
            <!-- Controls Header -->
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-5 h-5 text-gray-600">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
    />
</svg>

                <h3 class="text-lg font-semibold text-gray-900">
                  {{ $t('HeadContract.BillOfQuantities.trades') }}
                </h3>
              </div>

              <Transition
                mode="out-in"
                enter-active-class="animate__animated animate__fadeIn animate__faster"
                leave-active-class="animate__animated animate__fadeOut animate__faster"
              >
                <p
                  v-if="trades.length > 0"
                  key="total-trades"
                  class="text-sm text-gray-600 bg-white px-3 py-1 rounded-full border border-gray-200"
                >
                  <span class="font-medium text-gray-900 whitespace-pre">{{ trades.length }}&nbsp;</span>

                  <span class="text-gray-500">{{ $t('HeadContract.BillOfQuantities.tradeCount', trades.length) }}</span>
                </p>
              </Transition>
            </div>

            <!-- Action Controls -->
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <button
                  v-if="projectsStore.project?.unarchived && !isEditingDisabled"
                  type="button"
                  class="btn btn--secondary-blue flex items-center space-x-2"
                  @click="createTrade"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

                  <span>{{ $t('HeadContract.BillOfQuantities.button.addTrade') }}</span>
                </button>

                <!-- Expand to Depth Button -->
                <div
                  v-if="trades.length > 0"
                  class="relative"
                  @click.stop
                >
                  <button
                    type="button"
                    class="btn btn--secondary-blue flex items-center space-x-2"
                    @click="showExpandMenu = !showExpandMenu"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
</svg>

                    <span>Expand</span>
                  </button>

                  <div
                    v-if="showExpandMenu"
                    class="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10"
                    @click.stop
                  >
                    <button
                      type="button"
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      :class="{ 'bg-blue-50 text-blue-700': expandToDepth === 0 }"
                      @click="setExpandToDepth(0); showExpandMenu = false"
                    >
                      All Trades
                    </button>

                    <button
                      type="button"
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      :class="{ 'bg-blue-50 text-blue-700': expandToDepth === 1 }"
                      @click="setExpandToDepth(1); showExpandMenu = false"
                    >
                      Level 1
                    </button>

                    <button
                      type="button"
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      :class="{ 'bg-blue-50 text-blue-700': expandToDepth === 2 }"
                      @click="setExpandToDepth(2); showExpandMenu = false"
                    >
                      Level 2
                    </button>

                    <button
                      type="button"
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      :class="{ 'bg-blue-50 text-blue-700': expandToDepth === 3 }"
                      @click="setExpandToDepth(3); showExpandMenu = false"
                    >
                      Level 3
                    </button>

                    <button
                      type="button"
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      :class="{ 'bg-blue-50 text-blue-700': expandToDepth === Infinity }"
                      @click="setExpandToDepth(Infinity); showExpandMenu = false"
                    >
                      All Levels
                    </button>
                  </div>
                </div>

                <!-- Collapse Button -->
                <button
                  v-if="trades.length > 0 && expandToDepth !== undefined"
                  type="button"
                  class="btn btn--secondary-blue flex items-center space-x-2"
                  @click="collapseAll"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25" />
</svg>

                  <span>Collapse</span>
                </button>
              </div>

              <div class="text-right">
                <p class="text-gray-500 font-semibold">
                  {{ $t('HeadContract.BillOfQuantities.totalContractValue') }}
                </p>

                <div
                  v-if="contractAmount.adjustments !== 0 || contractAmount.provisionalSums !== 0"
                  class="text-sm flex-col flex font-semibold text-gray-900"
                >
                  <div>
                    <span>Works:</span>

                    <span class="ml-1 font-normal">
                      {{ australianCurrency(contractAmount.work) }}
                    </span>
                  </div>

                  <div v-if="contractAmount.provisionalSums !== 0">
                    <span>Provisional Sums:</span>

                    <span class="ml-1 font-normal">
                      {{ australianCurrency(contractAmount.provisionalSums) }}
                    </span>
                  </div>

                  <div v-if="contractAmount.adjustments !== 0">
                    <span>Adjustments:</span>

                    <span class="ml-1 font-normal">
                      {{ australianCurrency(-contractAmount.adjustments) }}
                    </span>
                  </div>

                  <div>
                    <span>Sum:</span>

                    <span class="ml-1 font-normal">
                      {{ australianCurrency(contractAmount.total) }}
                    </span>
                  </div>
                </div>

                <div
                  v-else
                  class="font-semibold text-gray-900"
                >
                  {{ australianCurrency(contractAmount.work) }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Trades List -->
        <div class="overflow-x-auto">
          <div
            v-if="trades.length > 0"
            class="w-full"
          >
            <Sortable
              :list="trades"
              item-key="id"
              class="divide-y divide-gray-200"
              :class="{ 'opacity-50 pointer-events-none': updateSortAction.is(States.LOADING) }"
              :disabled="disableDraggable || !projectsStore.project?.unarchived || isEditingDisabled"
              :options="{
                handle: '.drag-handle'
              }"
              @end="changeSortOrder"
            >
              <template
                v-if="updateSortAction.is(States.LOADING)"
                #header
              >
                <div class="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-1/2">
                  <AppSpinner />
                </div>
              </template>

              <template #item="{element}">
                <div class="group relative">
                  <TradeRow
                    :disable-draggable="!projectsStore.project?.unarchived"
                    :trade="element"
                    :expand-to-depth="expandToDepth"
                    class="peer"
                    @edit="selectTrade(element)"
                    @delete="deleteTrade(element)"
                    @modal-close="disableDraggable = false"
                    @modal-open="disableDraggable = true"
                    @create-trade-item="handleCreateTradeItem"
                    @edit-trade-item="handleEditTradeItem"
                  >
                    <template #default="{ open }">
                      <div
                        class="group-hover:bg-gray-50 py-0.5 transition-colors duration-150 ease-in-out"
                      >
                        <div :class="{ invisible: !projectsStore.project?.unarchived || isEditingDisabled }">
                          <button
                            v-if="open"
                            type="button"
                            class="mx-auto flex items-center justify-center space-x-1 cursor-pointer opacity-0 group-hover:opacity-100 text-xs font-medium border border-blue-200 rounded-lg px-2 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-300 transition-all duration-300 ease-in-out shadow-sm hover:shadow-md transform"
                            @click="handleCreateTradeItem({ trade: element })"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4 fill-blue-700">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

                            <span>{{ $t('HeadContract.BillOfQuantities.button.addTradeItem') }}</span>
                          </button>

                          <button
                            v-else
                            type="button"
                            class="mx-auto flex items-center justify-center space-x-1 cursor-pointer opacity-0 group-hover:opacity-100 text-xs font-medium border border-blue-200 rounded-lg px-2 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-300 transition-all duration-300 ease-in-out shadow-sm hover:shadow-md transform"
                            @click="showTradeAndSelectPosition(element.sort_order + 1)"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4 fill-blue-700">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

                            <span>{{ $t('HeadContract.BillOfQuantities.button.addTrade') }}</span>
                          </button>
                        </div>
                      </div>
                    </template>
                  </TradeRow>
                </div>
              </template>
            </Sortable>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div
        v-else
        class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-8"
      >
        <div class="px-6 py-16 text-center">
          <AppSpinner class="h-8 w-8 mx-auto mb-4" />

          <h3 class="text-sm font-medium text-gray-900 mb-1">
            {{ $t('HeadContract.BillOfQuantities.loadingTrades') }}
          </h3>
        </div>
      </div>
    </div>

    <TradeSaveModal
      v-if="showTradeModal"
      :trade="selectedTrade"
      :project-id="$props.projectId"
      :selected-position="selectedPosition"
      boq-type="direct"
      @update-sort="changeSortOrderForTrade($event.trade, $event.index)"
      @close="showTradeModal = false; selectedPosition = undefined"
    />

    <TradeItemSaveModal
      v-if="showTradeItemModal"
      :trade="selectedTradeForItem"
      :trade-item="selectedTradeItem"
      :parent-trade-item="selectedParentTradeItem"
      @update-sort="changeSortOrderForTradeItem($event.tradeItem, $event.index)"
      @close="closeTradeItemModal"
    />
  </div>
</template>

<script setup lang="ts">
import TradeItemSaveModal from '@/components/HeadContract/BillOfQuantities/TradeItemSaveModal.vue';
import TradeRow from '@/components/HeadContract/BillOfQuantities/TradeRow.vue';
import TradeSaveModal from '@/components/HeadContract/BillOfQuantities/TradeSaveModal.vue';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, falsey } from '@/helpers';
import Trade from '@/models/Trade';
import TradeItem from '@/models/TradeItem';
import { useProjectsStore } from '@/store/projects';
import { useTradeItemsStore } from '@/store/tradeItems';
import { useTradesStore } from '@/store/trades';
import { useUserStore } from '@/store/user';
import { Sortable } from 'sortablejs-vue3';
import Swal from 'sweetalert2';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import draggable from 'vuedraggable';

const props = defineProps<{
  projectId: string;
}>();

const tradesStore = useTradesStore();
const tradeItemsStore = useTradeItemsStore();
const fetchTradesAction = useStoreApiAction(tradesStore.fetchTrades);
const generateTradesAction = useStoreApiAction(tradesStore.generateTrades);

watch(() => props.projectId, () => {
  fetchTradesAction.request(props.projectId, { boq_type: 'direct' }).catch((error) => {
    console.log(error);
  });
}, { immediate: true });

const trades = computed(() => {
  return tradesStore.models
    .with('trade_items', (query) => {
      query.whereNull('parent_id');
    })
    .where('project_id', parseInt(props.projectId))
    .where('boq_type', 'direct')
    .orderBy('sort_order')
    .get();
});

const contractAmount = computed(() => {
  const contractAmount = {
    total: 0,
    work: 0,
    adjustments: 0,
    provisionalSums: 0,
  };

  trades.value.forEach((trade) => {
    contractAmount.total += trade.calculateTotalAdjustedBudget();
    contractAmount.work += trade.calculateTotalBudget();
    contractAmount.adjustments += trade.calculateTotalBudget() - trade.calculateTotalAdjustedBudget();
    contractAmount.provisionalSums += trade.calculateTotalProvisionalBudget();
  });

  if(contractAmount.provisionalSums > 0) {
    contractAmount.work -= contractAmount.provisionalSums;
  }

  return contractAmount;
});

const selectedTrade = ref<Trade | undefined>();
const selectedPosition = ref<number | undefined>();
const showTradeModal = ref(false);
// Trade Item Modal State
const selectedTradeForItem = ref<Trade | undefined>();
const selectedTradeItem = ref<TradeItem | undefined>();
const selectedParentTradeItem = ref<TradeItem | undefined>();
const showTradeItemModal = ref(false);

const showTradeAndSelectPosition = (position: number) => {
  selectedPosition.value = position;
  showTradeModal.value = true;
};

const selectTrade = (trade: Trade) => {
  selectedTrade.value = trade;
  showTradeModal.value = true;
};

const createTrade = () => {
  selectedTrade.value = undefined;
  showTradeModal.value = true;
};

const deleteTradeAction = useStoreApiAction(tradesStore.deleteTrade);

const deleteTrade = (trade: Trade) => {
  Swal.fire({
    icon: 'warning',
    title: 'Delete Trade?',
    text: `You are about to delete ${trade.name}. This action cannot be undone.`,
    showCancelButton: true,
    showConfirmButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      deleteTradeAction.request(trade.id).then(() => {
        Swal.fire({
          icon: 'warning',
          title: 'Trade Deleted.',
        });
      }).catch((error) => {
        if(error?.status === 422 && error?.data.errors.trade_items) {
          Swal.fire({
            icon: 'error',
            title: `Failed to delete trade ${trade.name}`,
            text: error?.data.errors.trade_items[0],
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: `Failed to delete trade ${trade.name}`,
          });
        }
      });
    }
  });
};

const updateSortAction = useStoreApiAction(tradesStore.updateSort);

const changeSortOrderForTrade = (trade: Trade, index: number) => {
  updateSortAction.request(trade.id, {
    sort_order: index,
  });
};

const updateTradeItemSortAction = useStoreApiAction(tradeItemsStore.updateSort);

const changeSortOrderForTradeItem = (tradeItem: TradeItem, index: number) => {
  updateTradeItemSortAction.request(tradeItem.id, {
    sort_order: index,
  });
};

const handleCreateTradeItem = (data: { trade: Trade; tradeItem?: TradeItem }) => {
  selectedTradeForItem.value = data.trade;
  selectedParentTradeItem.value = data.tradeItem;
  selectedTradeItem.value = undefined;
  showTradeItemModal.value = true;
  disableDraggable.value = true;
};

const closeTradeItemModal = () => {
  selectedTradeItem.value = undefined;
  selectedParentTradeItem.value = undefined;
  showTradeItemModal.value = false;
  disableDraggable.value = false;
};

const handleEditTradeItem = (data: { tradeItem: TradeItem; trade: Trade }) => {
  selectedTradeForItem.value = data.trade;
  selectedTradeItem.value = data.tradeItem;
  showTradeItemModal.value = true;
  disableDraggable.value = true;
};

const changeSortOrder = (e) => {
  const trade = trades.value.at(e.oldIndex);

  changeSortOrderForTrade(trade, e.newIndex + 1);
};

const projectsStore = useProjectsStore();
const lockBoqAction = useStoreApiAction(projectsStore.lockBoQ);

const project = computed(() => {
  return projectsStore.project;
});

// Computed property to check if editing should be disabled for contract managers when BOQ is locked
const isEditingDisabled = computed(() => {
  return userStore.isRole(['contract_manager'], false) && projectsStore.project?.boq_locked;
});

const canSeedTrades = computed(() => {
  return Boolean(
    projectsStore.project?.unarchived &&
      userStore.isRole(['admin', 'project_admin', 'company_admin', 'contract_admin']),
  );
});

const seedStandardTrades = () => {
  generateTradesAction.request(props.projectId).then(() => {
    return fetchTradesAction.request(props.projectId, { boq_type: 'direct' });
  }).then(() => {
    Swal.fire({
      icon: 'success',
      title: 'Trades generated',
      text: 'Standard trades have been added to this project.',
    });
  });
};

const lockBoq = () => {
  Swal.fire({
    icon: 'warning',
    title: 'Lock Bill of Quantities?',
    text: `Are you sure you want to lock the bill of quantities for ${project.value.name}?`,

    showCancelButton: true,
    showConfirmButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      lockBoqAction.request(props.projectId).then(() => {
        Swal.fire({
          icon: 'success',
          title: 'Locked',
          text: `Bill of quantities for ${project.value.name} has been locked.`,
        });

        fetchTradesAction.request(props.projectId, { boq_type: 'direct' }).catch((error) => {
          console.log(error);
        });
      }).catch((error) => {
        Swal.fire({
          icon: 'error',
          title: 'Failed to lock BoQ',
          text: error.data?.message,
        });
      });
    }
  });
};

const unlockBoqAction = useStoreApiAction(projectsStore.unlockBoq);

const unlockBoq = () => {
  Swal.fire({
    icon: 'warning',
    title: 'Unlock Bill of Quantities?',
    text:
      `Are you sure you want to unlock the bill of quantities for ${project.value.name}? Any new items will require a baseline to be set before you can lock again.`,

    showCancelButton: true,
    showConfirmButton: true,
    confirmButtonText: 'Unlock',
  }).then((result) => {
    if(result.isConfirmed) {
      unlockBoqAction.request(props.projectId).then(() => {
        Swal.fire({
          icon: 'success',
          title: 'Unlocked',
          text: `Bill of quantities for ${project.value.name} has been unlocked.`,
        });

        fetchTradesAction.request(props.projectId, { boq_type: 'direct' }).catch((error) => {
          console.log(error);
        });
      }).catch((error) => {
        Swal.fire({
          icon: 'error',
          title: 'Failed to unlock BoQ',
          text: error.data?.message,
        });
      });
    }
  });
};

const disableDraggable = ref(false);
const expandToDepth = ref<number | undefined>(undefined);
const showExpandMenu = ref(false);
const userStore = useUserStore();

const setExpandToDepth = (depth: number) => {
  expandToDepth.value = depth;
};

const collapseAll = () => {
  expandToDepth.value = undefined;
};

// Close expand menu when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;

  if(!target.closest('.relative')) {
    showExpandMenu.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped></style>
